/**
 * Como cada bloco do fluxo se explica em uma linha, e o que ainda falta nele.
 *
 * Antes o cartão no canvas mostrava só o rótulo ("Mensagem", "Ação"), então
 * entender um fluxo de dez passos exigia abrir os dez. E a única forma de
 * descobrir que faltava algo era clicar em Salvar e ler uma fila de avisos
 * que não dizia onde.
 *
 * As duas funções aqui leem o MESMO `node.data` que o motor recebe como
 * `config`. Se o formato mudar de um lado, muda dos dois — por isso vivem em
 * shared/ e não dentro de um componente.
 */

export type DadosDeNo = Record<string, any>;

export type PendenciaDeNo = {
  /** Uma linha curta para o cartão: "Falta o texto". */
  curta: string;
  /** A frase do painel "Antes de ativar", que explica o que fazer. */
  longa: string;
  /**
   * `false` quando a pessoa não resolve isto preenchendo um campo — bloco
   * descontinuado, por exemplo. Nesses casos travar o Salvar deixaria o fluxo
   * inteiro impossível de editar. Ausente = bloqueia.
   */
  bloqueia?: boolean;
};

const aspas = (t: string) => `“${t}”`;

const UNIDADES: Record<string, [string, string]> = {
  seconds: ["segundo", "segundos"],
  minutes: ["minuto", "minutos"],
  hours: ["hora", "horas"],
  days: ["dia", "dias"],
};

const TIPO_DE_ARQUIVO: Record<string, string> = {
  image: "imagem",
  video: "vídeo",
  audio: "áudio",
  file: "arquivo",
};

const DADO_COLETADO: Record<string, string> = {
  email: "e-mail",
  phone: "telefone",
  cpf: "CPF",
  number: "número",
  text: "resposta",
};

const ACAO_DE_CONTATO: Record<string, string> = {
  "add-tag": "Marca o contato",
  add_tag: "Marca o contato",
  "remove-tag": "Tira a etiqueta do contato",
  remove_tag: "Tira a etiqueta do contato",
};

const OPERADOR: Record<string, string> = {
  equals: "for",
  "not-equals": "não for",
  contains: "contiver",
  greater: "for maior que",
  less: "for menor que",
};

/** Espelho de shared/close-reasons. Aqui só para o resumo do cartão. */
const MOTIVO_DE_ENCERRAMENTO: Record<string, string> = {
  resolved: "demanda atendida",
  client_confirmed: "cliente confirmou a solução",
  no_response: "cliente não respondeu",
  out_of_scope: "fora do escopo",
  duplicate: "atendimento duplicado",
  spam: "spam ou erro de número",
  transferred: "encaminhado a outra equipe",
  other: "outro motivo",
};

const CAMPO_DA_CONDICAO: Record<string, string> = {
  tag: "a etiqueta",
  field: "o campo",
  status: "o status",
  engagement: "o engajamento",
};

/** Nome amigável de um arquivo a partir da url gravada no nó. */
export function nomeDoArquivo(url: string | undefined | null): string {
  const limpa = (url ?? "").trim();
  if (!limpa) return "";
  // /api/media/37 não tem nome no path — quem sabe o nome é o banco, e o
  // cartão não tem como perguntar. Mostrar a url crua como se fosse texto
  // útil era pior que dizer "arquivo enviado".
  if (/^\/api\/media\/\d+/.test(limpa)) return "arquivo enviado";
  try {
    return decodeURIComponent(limpa.split("?")[0]!.split("/").pop() || "") || limpa;
  } catch {
    return limpa;
  }
}

function resumoDaMensagem(d: DadosDeNo): string {
  if (d.contentSource === "template") {
    return d.templateName ? `Envia o modelo ${aspas(d.templateName)}` : "Escolha o modelo aprovado";
  }
  const mc = d.messageContent ?? {};
  const texto = String(mc.text ?? "").trim();

  if (mc.type && mc.type !== "text") {
    if (!String(mc.url ?? "").trim()) return `Escolha o ${TIPO_DE_ARQUIVO[mc.type] ?? "arquivo"} a enviar`;
    const rotulo = TIPO_DE_ARQUIVO[mc.type] ?? "arquivo";
    // `fileName` é o nome original do upload; a url de arquivo nosso é só
    // /api/media/25 e não diz nada a quem lê o cartão.
    const nome = String(mc.fileName ?? "").trim() || nomeDoArquivo(mc.url);
    const legenda = String(mc.caption ?? "").trim() || texto;
    return legenda ? `Envia ${rotulo}: ${nome} — ${legenda}` : `Envia ${rotulo}: ${nome}`;
  }

  return texto || "Escreva o que será enviado";
}

function resumoDaAcao(d: DadosDeNo): string {
  const tipo = String(d.actionType ?? "add-tag");

  if (tipo === "assign" || tipo === "assign_agent" || tipo === "handoff") {
    const para = tipo === "handoff" || d.stopBot ? "Entrega a conversa" : "Atribui a conversa";
    // Modo "pessoa fixa" sem ninguém escolhido não é distribuição automática —
    // é um passo incompleto. Dizer o contrário aqui fazia o cartão prometer um
    // comportamento que o motor vai recusar.
    if (d.assignMode === "user" && !d.assignedUserId) return "Escolha quem recebe a conversa";
    if (d.assignMode === "team") {
      const n = Array.isArray(d.teamUserIds) ? d.teamUserIds.length : 0;
      return n > 0
        ? `${para} para quem estiver menos ocupado entre ${n} ${n === 1 ? "pessoa" : "pessoas"}`
        : "Escolha quem faz parte da equipe";
    }
    const alvo =
      d.assignedUserId || d.assignedUserName
        ? String(d.assignedUserName || `atendente #${d.assignedUserId}`)
        : "quem a distribuição automática escolher";
    return `${para} para ${alvo}`;
  }

  if (tipo === "create_deal") {
    const onde = [d.pipelineName, d.stageName].filter(Boolean).join(" · ");
    return onde ? `Abre oportunidade em ${onde}` : "Escolha o funil e a etapa";
  }

  if (tipo === "move_deal_stage") {
    return d.stageName ? `Move a oportunidade para ${aspas(String(d.stageName))}` : "Escolha a etapa de destino";
  }

  if (tipo === "send_proposal") {
    return "Envia a proposta aprovada do negócio";
  }

  if (tipo === "note") {
    const t = String(d.value ?? d.text ?? "").trim();
    return t ? `Nota no Inbox: ${t}` : "Escreva a nota interna";
  }

  if (tipo === "timeline_event") {
    return d.customEventName
      ? `Registra ${aspas(String(d.customEventName))} na linha do tempo`
      : "Escolha o evento a registrar";
  }

  if (tipo === "close_conversation") {
    const rotulo = MOTIVO_DE_ENCERRAMENTO[String(d.closeReason ?? "resolved")];
    return rotulo ? `Encerra o atendimento: ${rotulo.toLowerCase()}` : "Escolha o motivo do encerramento";
  }

  const acaoDeEtiqueta = ACAO_DE_CONTATO[tipo];
  if (acaoDeEtiqueta) {
    // `value` guarda os NOMES (o painel escreve ao escolher), `tagIds` guarda
    // os ids. Ler só `value` fazia um bloco corretamente configurado dizer
    // "Escolha a etiqueta" no cartão enquanto a pendência dizia que estava
    // pronto — duas respostas diferentes para a mesma pergunta.
    const nomes = String(d.value ?? "").trim();
    if (nomes) return `${acaoDeEtiqueta} com ${aspas(nomes)}`;
    const quantas = Array.isArray(d.tagIds) ? d.tagIds.length : d.tagId ? 1 : 0;
    if (quantas > 0) {
      return `${acaoDeEtiqueta} com ${quantas} ${quantas === 1 ? "etiqueta" : "etiquetas"}`;
    }
    return "Escolha a etiqueta";
  }

  if (tipo === "set-field" || tipo === "update_field") {
    const campo = String(d.field ?? "").trim();
    const valor = String(d.value ?? "").trim();
    if (!campo) return "Escolha o campo a atualizar";
    return valor ? `Grava ${aspas(valor)} em ${campo}` : `Limpa o campo ${campo}`;
  }

  if (tipo === "webhook" || tipo === "webhook_action") {
    const url = String(d.value ?? d.url ?? "").trim();
    return url ? `Avisa ${url}` : "Informe a URL de destino";
  }

  if (tipo === "update_score") {
    const p = Number(d.points ?? 0);
    if (!p) return "Defina quantos pontos somar";
    return p > 0 ? `Soma ${p} ponto${p > 1 ? "s" : ""} ao contato` : `Tira ${Math.abs(p)} ponto${Math.abs(p) > 1 ? "s" : ""} do contato`;
  }

  if (tipo === "enroll_in_flow") {
    return d.targetFlowName ? `Manda o contato para o fluxo ${aspas(String(d.targetFlowName))}` : "Escolha o fluxo de destino";
  }

  return "Escolha o que este passo faz";
}

function resumoDaEspera(d: DadosDeNo): string {
  if ((d.delayMode ?? "duration") === "until_time") {
    return d.untilTime ? `Espera até as ${d.untilTime}` : "Escolha o horário";
  }
  const n = Number(d.duration);
  if (!n || n <= 0) return "Defina quanto tempo esperar";
  const [sing, plur] = UNIDADES[String(d.unit ?? "minutes")] ?? UNIDADES.minutes!;
  return `Espera ${n} ${n === 1 ? sing : plur}${d.respectOfficeHours ? ", dentro do horário comercial" : ""}`;
}

/** Uma frase, em português de gente, dizendo o que o bloco faz. */
export function resumoDoNo(tipo: string | undefined, dados: DadosDeNo | undefined): string {
  const d = dados ?? {};
  switch (tipo) {
    case "content":
      return resumoDaMensagem(d);
    case "action":
      return resumoDaAcao(d);
    case "condition": {
      const cond = String(d.conditionType ?? d.field ?? "tag");
      if (cond === "ad_source") {
        const anuncio = String(d.adId ?? d.value ?? "").trim();
        return anuncio
          ? `Segue por Sim quem veio do anúncio ${aspas(anuncio)}`
          : "Segue por Sim quem veio de algum anúncio";
      }
      if (cond === "score" || cond === "score_total") {
        const op = { gte: "for pelo menos", gt: "passar de", lte: "for no máximo", lt: "for menor que", eq: "for exatamente", ne: "for diferente de" }[String(d.scoreOperator ?? "gte")] ?? "for pelo menos";
        return `Segue por Sim quando a pontuação ${op} ${Number(d.scoreValue ?? 50)}`;
      }
      const campo = CAMPO_DA_CONDICAO[String(d.field ?? "tag")] ?? "o campo";
      const op = OPERADOR[String(d.operator ?? "equals")] ?? "for";
      const valor = String(d.value ?? "").trim();
      return valor ? `Segue por Sim quando ${campo} ${op} ${aspas(valor)}` : "Defina a regra do Sim e do Não";
    }
    case "delay":
      return resumoDaEspera(d);
    case "randomizer": {
      const a = Number(d.path1Probability ?? 50);
      return `Manda ${a}% pelo caminho A e ${100 - a}% pelo B`;
    }
    case "ai_assistant": {
      const p = String(d.systemPrompt ?? "").trim();
      return p || "Diga à IA como ela deve responder";
    }
    case "ai_classifier": {
      const cats = (Array.isArray(d.categories) ? d.categories : []).map((c: string) => String(c).trim()).filter(Boolean);
      return cats.length >= 2
        ? `Separa a conversa em: ${cats.join(", ")}`
        : "Liste pelo menos duas categorias";
    }
    case "data_collection": {
      const pergunta = String(d.promptMessage ?? "").trim();
      const dado = DADO_COLETADO[String(d.fieldType ?? "text")] ?? "resposta";
      if (!pergunta) return `Escreva a pergunta que coleta o ${dado}`;
      return d.saveToField ? `${pergunta} → guarda em ${d.saveToField}` : `${pergunta} → guarda o ${dado}`;
    }
    case "webhook_request": {
      const url = String(d.url ?? "").trim();
      return url ? `${String(d.method ?? "POST").toUpperCase()} ${url}` : "Informe a URL de destino";
    }
    case "target": {
      const v = String(d.targetValue ?? "").trim();
      return v ? `Filtra por ${v} (bloco descontinuado)` : "Bloco descontinuado — troque por Condição";
    }
    case "end":
      return "O fluxo termina aqui";
    case "trigger":
      return "Começo do fluxo";
    default:
      return "";
  }
}

/**
 * O que impede o fluxo de rodar. `null` quando o bloco está pronto.
 *
 * Espelha as checagens que o motor faz em tempo de execução — a diferença é
 * que aqui a pessoa descobre antes de ativar, e não por uma execução que
 * falhou calada em produção.
 */
export function pendenciaDoNo(tipo: string | undefined, dados: DadosDeNo | undefined): PendenciaDeNo | null {
  const d = dados ?? {};
  const falta = (curta: string, longa: string): PendenciaDeNo => ({ curta, longa });

  switch (tipo) {
    case "content": {
      if (d.contentSource === "template") {
        return d.templateId ? null : falta("Falta o modelo", "Escolha qual modelo aprovado pela Meta este bloco envia.");
      }
      const mc = d.messageContent ?? {};
      const temTexto = !!String(mc.text ?? "").trim();
      // Qualquer botão exige corpo: a Meta recusa mensagem interativa (botões
      // de resposta) e CTA (um botão de link) sem texto, e a legenda do
      // arquivo não serve de corpo. Antes só os de resposta eram checados, e o
      // nó com arquivo + botão de link mandava a mídia e falhava logo depois.
      const botoes = (mc.buttons ?? []) as Array<Record<string, any>>;
      const deResposta = botoes.filter((b) => b?.type === "text" || b?.type === "postback");
      const deLink = botoes.filter((b) => b?.type === "url" && !!b?.value);
      const deTelefone = botoes.filter((b) => b?.type === "phone" && !!b?.value);
      // As duas formas em que a Meta exige corpo: mensagem interativa (botões
      // de resposta) e CTA nativo (exatamente um link e nenhum telefone —
      // é assim que o motor decide mandar CTA). Nos demais casos os links
      // viram texto no fim do corpo, e aí já existe corpo.
      const exigeCorpo = deResposta.length > 0 || (deLink.length === 1 && deTelefone.length === 0);

      if (mc.type && mc.type !== "text") {
        if (!String(mc.url ?? "").trim()) {
          const rotulo = TIPO_DE_ARQUIVO[mc.type] ?? "arquivo";
          return falta("Falta o arquivo", `Envie o ${rotulo} ou cole uma URL https pública — sem isso o bloco não tem o que mandar.`);
        }
        if (exigeCorpo && !temTexto) {
          return falta(
            "Falta o texto dos botões",
            "Com botão, o WhatsApp exige uma mensagem de texto junto — a legenda do arquivo não conta.",
          );
        }
        return null;
      }
      return temTexto ? null : falta("Falta o texto", "Escreva a mensagem que o contato vai receber.");
    }

    case "action": {
      const tipoAcao = String(d.actionType ?? "");
      if (!tipoAcao) return falta("Falta escolher a ação", "Diga o que este bloco faz com o contato.");
      if (tipoAcao === "add-tag" || tipoAcao === "remove-tag" || tipoAcao === "add_tag" || tipoAcao === "remove_tag") {
        const temTags = (Array.isArray(d.tagIds) && d.tagIds.length > 0) || !!d.tagId || String(d.value ?? "").trim();
        return temTags ? null : falta("Falta a etiqueta", "Escolha pelo menos uma etiqueta para este bloco aplicar.");
      }
      if (tipoAcao === "assign" || tipoAcao === "assign_agent" || tipoAcao === "handoff") {
        if (d.assignMode === "team") {
          return Array.isArray(d.teamUserIds) && d.teamUserIds.length > 0
            ? null
            : falta("Falta a equipe", "Escolha quem faz parte da equipe que recebe estas conversas.");
        }
        // Só cobra quando a pessoa ESCOLHEU "uma pessoa fixa" e não escolheu
        // ninguém. Sem `assignMode` é nó antigo: o motor o roda como
        // distribuição automática (ver `executeActionNode`) e `resumoDaAcao`
        // diz o mesmo — cobrar aqui faria as duas funções deste arquivo, que
        // existem justamente para concordar, dizerem coisas opostas. E o
        // `value` desses nós pode ser qualquer texto, porque era o campo
        // livre "agent-id" da antiga "Atribuir Agente".
        if (d.assignMode !== "user") return null;
        return d.assignedUserId
          ? null
          : falta(
              "Falta o atendente",
              "Escolha quem recebe a conversa, ou deixe a distribuição automática decidir.",
            );
      }
      if (tipoAcao === "create_deal") {
        return d.pipelineId && d.stageId
          ? null
          : falta("Falta o funil", "Escolha em qual funil e etapa a oportunidade nasce.");
      }
      if (tipoAcao === "move_deal_stage") {
        return d.stageId ? null : falta("Falta a etapa", "Escolha para qual etapa a oportunidade vai.");
      }
      if (tipoAcao === "send_proposal") {
        // Depende da proposta existir no negócio, o que só se sabe em execução.
        return null;
      }
      if (tipoAcao === "note") {
        return String(d.value ?? d.text ?? "").trim()
          ? null
          : falta("Falta o texto", "Escreva a nota que fica registrada no Inbox.");
      }
      if (tipoAcao === "timeline_event") {
        return d.customEventId
          ? null
          : falta("Falta o evento", "Escolha qual evento personalizado este passo registra.");
      }
      if (tipoAcao === "close_conversation") {
        return d.closeReason
          ? null
          : falta("Falta o motivo", "Diga por que o atendimento está sendo encerrado — é o que alimenta o relatório.");
      }
      if (tipoAcao === "update_score") {
        const pontos = Number(d.points ?? 0);
        // `Number.isFinite` porque NaN passa em `!== 0`.
        return Number.isFinite(pontos) && pontos !== 0
          ? null
          : falta("Falta a pontuação", "Diga quantos pontos somar ou tirar — zero não muda nada.");
      }
      if (tipoAcao === "enroll_in_flow") {
        return d.targetFlowId
          ? null
          : falta("Falta o fluxo", "Escolha para qual fluxo o contato segue.");
      }
      if (tipoAcao === "set-field" || tipoAcao === "update_field") {
        if (String(d.field ?? "").trim()) return null;
        // Aviso, não bloqueio: o editor antigo nem perguntava o campo, então
        // há nós assim em fluxos que rodam há meses. Travar o Salvar deixaria
        // a pessoa sem conseguir corrigir nem outra coisa no mesmo fluxo.
        return {
          curta: "Falta o campo",
          longa: "Escolha qual campo do contato este bloco atualiza — sem ele, o passo não roda.",
          bloqueia: false,
        };
      }
      return String(d.value ?? d.url ?? "").trim()
        ? null
        : falta("Falta o valor", "Preencha o valor desta ação.");
    }

    case "condition": {
      const cond = String(d.conditionType ?? d.field ?? "tag");
      // "Veio de anúncio?" sem anúncio específico é uma pergunta válida, e a
      // pontuação já tem alvo próprio — nenhuma das duas usa `value`.
      if (cond === "ad_source") return null;
      if (cond === "score" || cond === "score_total") {
        return Number.isFinite(Number(d.scoreValue))
          ? null
          : falta("Falta a pontuação", "Diga a partir de quantos pontos o contato segue pelo Sim.");
      }
      if (cond === "composite") {
        // Condição composta vem do construtor de e-mail e tem forma própria;
        // o painel de WhatsApp não a edita. Não cobrar `value` dela.
        return null;
      }
      if (cond === "field" && !String(d.fieldKey ?? "").trim()) {
        // Sem o campo, o motor lê `contato[undefined]` e todo mundo segue pelo
        // Não — em silêncio, que é o pior jeito de uma condição errar.
        return falta("Falta o campo", "Diga qual campo do contato comparar.");
      }
      return String(d.value ?? "").trim()
        ? null
        : falta("Falta a regra", "Diga com o que comparar — sem isso todo mundo cai no mesmo caminho.");
    }

    case "delay":
      if ((d.delayMode ?? "duration") === "until_time") {
        return d.untilTime ? null : falta("Falta o horário", "Escolha a que horas o fluxo continua.");
      }
      return Number(d.duration) > 0
        ? null
        : falta("Falta o tempo", "Diga quanto tempo esperar — tem que ser maior que zero.");

    case "ai_assistant":
      return String(d.systemPrompt ?? "").trim()
        ? null
        : falta("Falta a instrução", "Diga à IA qual é o papel dela e o que ela pode responder.");

    case "ai_classifier": {
      const cats = (Array.isArray(d.categories) ? d.categories : []).filter((c: string) => String(c).trim());
      return cats.length >= 2
        ? null
        : falta("Faltam categorias", "Liste pelo menos duas categorias — com uma só não há o que separar.");
    }

    case "data_collection": {
      if (!d.fieldType) return falta("Falta o tipo de dado", "Diga que informação este bloco coleta.");
      return String(d.promptMessage ?? "").trim()
        ? null
        : falta("Falta a pergunta", "Escreva a pergunta que o contato vai receber.");
    }

    case "webhook_request":
      return String(d.url ?? "").trim()
        ? null
        : falta("Falta a URL", "Informe para onde os dados vão.");

    case "target":
      // Nunca existiu no motor: era espelhado como mensagem vazia e derrubava
      // a execução. Hoje vira passagem, mas continua sem fazer nada.
      //
      // Aviso, não bloqueio: quem tem um destes num fluxo antigo precisa
      // conseguir abrir e salvar para trocá-lo.
      return {
        curta: "Bloco descontinuado",
        longa: "O bloco Público-Alvo não é executado pelo motor. Troque por Condição e apague este.",
        bloqueia: false,
      };

    default:
      return null;
  }
}
